import { useEffect, useMemo, useState } from 'react';
import { flattenLeafCategories } from '@/shein-platform/utils/sheinCategoryTree';
import {
  isOfficialTemplateScope,
  isPersonalTemplateScope,
  isTeamTemplateScope,
  SHEIN_TEMPLATE_SCOPES,
} from '@/shein-platform/constants/sheinTemplateScope';
import { isSheinShopAuthorized } from '@/shein-platform/constants/sheinRoutes';
import { useSheinShop } from '@/shein-platform/context/SheinShopContext';
import { sheinPlatformAPI, fundingAuthAPI, isRequestAborted, getIpeWorkspaceMode } from '@/utils/api';
import { isTeamSpaceEntryAvailable } from '@/pages/ipe/workbench/utils/accountCenterUtils';
import {
  isAllShopsScope,
  isConcreteShopId,
} from '@/utils/platformShopScope';
import useSheinOfficialShopCategoryScopes from '@/shein-platform/hooks/useSheinOfficialShopCategoryScopes';

/**
 * 模板库：单店铺独享按当前店铺 ID；团队店铺共享按当前店类目在工作区可见（不按店铺 ID 隔离）。
 * 各列表页自行管理 keyword / page / 勾选，避免把表格状态绑死在 hook 里。
 *
 * @param {{ loadCategories?: boolean, preferredShopId?: string }} [options]
 *   preferredShopId：页内店铺维度选中值；「全部」时路径仍锚到一家具体店，并带 allShops=true
 */
export default function useSheinTemplateScope({ loadCategories = true, preferredShopId } = {}) {
  const { activeShop, activeShopId, shops, scopedShops } = useSheinShop();

  const authShopId = useMemo(() => {
    if (isConcreteShopId(preferredShopId)) return String(preferredShopId);
    if (activeShop?.id != null && isConcreteShopId(activeShop.id)) return String(activeShop.id);
    if (isConcreteShopId(activeShopId)) return String(activeShopId);
    // 顶栏/页内为「全部」时：API 路径仍需要合法数字 shopId，锚到同类型第一家已授权店
    const pool = (Array.isArray(scopedShops) && scopedShops.length
      ? scopedShops
      : (shops || []).filter((s) => isSheinShopAuthorized(s)));
    const first = pool.find((s) => s?.id != null);
    return first?.id != null ? String(first.id) : '';
  }, [preferredShopId, activeShop, activeShopId, shops, scopedShops]);

  const listAllShops = preferredShopId != null && String(preferredShopId) !== ''
    ? isAllShopsScope(preferredShopId)
    : isAllShopsScope(activeShopId);

  const [scopeTab, setScopeTab] = useState(SHEIN_TEMPLATE_SCOPES.personal);
  const [categories, setCategories] = useState([]);
  const [canAccessTeam, setCanAccessTeam] = useState(false);
  const [workspaceMode, setWorkspaceMode] = useState(() => getIpeWorkspaceMode());

  useEffect(() => {
    const sync = (ev) => {
      const next = ev?.detail?.mode;
      if (next === 'personal' || next === 'team') {
        setWorkspaceMode(next);
        return;
      }
      setWorkspaceMode(getIpeWorkspaceMode());
    };
    window.addEventListener('ipe:workspace-mode-changed', sync);
    window.addEventListener('ipe-workspace-mode-changed', sync);
    window.addEventListener('ipe:workspace-refresh', sync);
    return () => {
      window.removeEventListener('ipe:workspace-mode-changed', sync);
      window.removeEventListener('ipe-workspace-mode-changed', sync);
      window.removeEventListener('ipe:workspace-refresh', sync);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    fundingAuthAPI.getAccountCenter()
      .then((center) => {
        if (!cancelled) setCanAccessTeam(isTeamSpaceEntryAvailable(center));
      })
      .catch((err) => {
        if (!cancelled && !isRequestAborted(err)) setCanAccessTeam(false);
      });
    return () => { cancelled = true; };
  }, [workspaceMode]);

  useEffect(() => {
    if (!canAccessTeam && isTeamTemplateScope(scopeTab)) {
      setScopeTab(SHEIN_TEMPLATE_SCOPES.personal);
    }
  }, [canAccessTeam, scopeTab]);

  const listShopId = authShopId;
  const categoryShopId = authShopId;
  const canQuery = Boolean(authShopId);
  const isOfficialTab = isOfficialTemplateScope(scopeTab);
  const isPersonalTab = isPersonalTemplateScope(scopeTab);

  const officialCategoryScopes = useSheinOfficialShopCategoryScopes({
    shops: scopedShops,
    enabled: listAllShops && isOfficialTab && loadCategories,
  });

  const categoryOptions = useMemo(
    () => flattenLeafCategories(categories).map((opt) => ({
      value: opt.categoryId,
      label: opt.label,
      productTypeId: opt.productTypeId,
    })),
    [categories],
  );

  useEffect(() => {
    if (!loadCategories) {
      setCategories([]);
      return undefined;
    }
    if (listAllShops && isOfficialTab) {
      setCategories(officialCategoryScopes.mergedCategoryTree);
      return undefined;
    }
    if (!categoryShopId) {
      setCategories([]);
      return undefined;
    }
    let cancelled = false;
    sheinPlatformAPI.getShopCategories(categoryShopId)
      .then((res) => {
        if (cancelled) return;
        setCategories(res?.categories ?? res?.items ?? res?.list ?? res ?? []);
      })
      .catch(() => {
        if (!cancelled) setCategories([]);
      });
    return () => { cancelled = true; };
  }, [
    loadCategories,
    categoryShopId,
    listAllShops,
    isOfficialTab,
    officialCategoryScopes.mergedCategoryTree,
  ]);

  const listQueryParams = (extra = {}) => ({
    scope: scopeTab,
    ...(listAllShops ? { allShops: true } : {}),
    ...extra,
  });

  const changeScopeTab = (tabId, { onReset } = {}) => {
    if (scopeTab === tabId) return;
    setScopeTab(tabId);
    onReset?.();
  };

  return {
    authShopId,
    scopeTab,
    setScopeTab,
    changeScopeTab,
    listShopId,
    categoryShopId,
    listAllShops,
    canQuery,
    isOfficialTab,
    isPersonalTab,
    canAccessTeam,
    categories,
    setCategories,
    categoryOptions,
    listQueryParams,
    loadCategories: Boolean(loadCategories),
    officialCategoryScopesLoading: officialCategoryScopes.loading,
    shopsForOfficialTemplate: officialCategoryScopes.shopsForOfficialTemplate,
    shopsForOfficialTemplates: officialCategoryScopes.shopsForOfficialTemplates,
  };
}
